'use client';

import { IProductListItem } from '@/api/types/product';
import Image from '@/components/Image';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import ProductCard from '@/components/Product/ProductCard';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
import { handleListLayout } from '@/utils/goods-list-utils';
import { isEmpty } from '@/utils/tools';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AdvCard from './AdvCard';
import { useTranslations } from 'next-intl';

interface ProductListProps {
  listData: IProductListItem[];
  total: number;
  allTotal: number;
  noData: boolean;
  loading: boolean;
  isWeb: boolean;
  column: number;
  isAcc: boolean;
  imageRule: any;
  imageHoverRule?: any;
  infiniteScrollPage: number;
  limit: number;
  handleReachBottom: () => Promise<void>;
  page: number;
  handlePageChange: (page: number) => void;
  keywords?: string;
  handleProductClick?: (index: number, product: any) => void;
  handleProductInView?: (index: number, product: any) => void;
  handleProductValidInView?: (index: number, product: any) => void;
  SDProductListPageTryon_Click?: (product: any, index: number) => void;
  sdProductListPageADExp?: (product: any, index: number) => void;
  sdProductListPageADClick?: (product: any, index: number) => void;
  currentFilter?: any;
  showNoMore?: boolean;
  isCrowdRecommendation?: boolean;
}

export default function ProductList({
  listData,
  total,
  allTotal,
  noData,
  loading,
  isWeb,
  column,
  isAcc,
  imageRule,
  imageHoverRule,
  infiniteScrollPage,
  limit,
  page,
  showNoMore,
  handleProductClick,
  handleProductInView = () => {},
  handleProductValidInView = () => {},
  handleReachBottom,
  sdProductListPageADClick,
  sdProductListPageADExp,
  handlePageChange,

  keywords,
  SDProductListPageTryon_Click,
  currentFilter,
  isCrowdRecommendation = false,
}: ProductListProps) {
  const [listDataWithAdv, setListDataWithAdv] = useState<IProductListItem[]>(listData || []);
  const t = useTranslations('goodsList.productList');
  useEffect(() => {
    setListDataWithAdv(listData);
  }, [listData]);

  // 计算网格样式
  const getGridClass = () => {
    if (isWeb) {
      return 'min-h-[400px] grid grid-cols-3 gap-y-[64px] gap-x-[16px] max-[1200px]:grid-cols-2 max-[640px]:flex max-[640px]:flex-col';
    } else if (column === 1) {
      return 'min-h-[30vh] flex flex-col px-14 gap-x-[4px] gap-y-[40px] mx-auto';
    } else {
      return 'min-h-[30vh] grid grid-cols-2 gap-x-[4px] gap-y-[40px]';
    }
  };

  // 计算listData的排列
  const handleListDataWithAdv = useCallback(
    (_column: number) => {
      const listCopy = [...listData];
      // 如果是1列布局，直接返回
      if (_column === 1) {
        setListDataWithAdv(listCopy);
        return;
      }
      // 处理列表布局
      const list = handleListLayout(listCopy, _column);
      setListDataWithAdv(list);
    },
    [listData],
  );

  // 初始化和窗口大小变化处理
  useEffect(() => {
    if (!isWeb) {
      handleListDataWithAdv(column);
      return;
    }
    const handleResize = () => {
      let _column = 3;
      if (window.innerWidth <= 1200) {
        _column = 2;
      }
      if (window.innerWidth <= 640) {
        _column = 1;
      }
      handleListDataWithAdv(_column);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isWeb, handleListDataWithAdv, column]);

  // 专门处理listData变化的useEffect
  useEffect(() => {
    if (!isWeb) {
      handleListDataWithAdv(column);
    } else {
      // Web端根据当前窗口大小计算列数
      let _column = 3;
      if (typeof window !== 'undefined') {
        if (window.innerWidth <= 1200) {
          _column = 2;
        }
        if (window.innerWidth <= 640) {
          _column = 1;
        }
      }
      handleListDataWithAdv(_column);
    }
  }, [listData, column, isWeb, handleListDataWithAdv]);

  const imageClassName = useMemo(() => {
    if (column === 1 && !isWeb) {
      return 'rounded-12 !aspect-[4/3]';
    }
    if (isWeb) {
      return '!aspect-[4/3] !min-h-[264px]';
    }
    if (!isWeb && isAcc) {
      return '!aspect-[1/1]';
    }
    return '';
  }, [column, isWeb]);
  // 无数据状态
  if (listData.length === 0 && noData) {
    return (
      <div className="no-match pt-[50px] pb-[100px] text-center">
        {!keywords && (
          <Image
            className="no-data mx-auto mb-[10px] w-[118px]"
            src="/goods-list/no-data.png"
            alt="No products"
            width={118}
            height={118}
            fetchPriority="high"
          />
        )}

        <p className="text-14 text-[#999999]">
          {t('noProductsMatching', {
            type: isEmpty(currentFilter) ? t('category') : t('selection'),
          })}
        </p>
      </div>
    );
  }
  // 无商品时返回null
  if (listData.length === 0) {
    return (
      <>
        <p className="results h-[38px] leading-[38px] pl-14 text-12" />
        <div className={`min-h-[30vh] grid pb-40 ${getGridClass()}`}>
          <SkeletonProductCard />
          <SkeletonProductCard />
          <SkeletonProductCard />
          <div className="min-[1200px]:hidden">
            <SkeletonProductCard />
          </div>
        </div>
      </>
    );
  }

  // 计算顶部位置类名
  const getTopClassName = () => {
    return 'border-b border-gray-200 top-[21.333vw]';
  };
  const getPicWidth = () => {
    if (isWeb) {
      return '70%';
    }
    return column === 1 ? '70%' : '80%';
  };
  return (
    <>
      {/* 结果统计 */}
      {!isWeb && (
        <>
          {keywords && (
            <p
              className={`results bg-white sticky z-3 px-14 py-10 font-bb text-[13px] ${getTopClassName()}`}
            >
              {t('searchResultsFor', { keywords })}
            </p>
          )}
          <p className="results h-[38px] leading-[38px] pl-14 text-12">
            {t('showingTotal', { total, result: total > 1 ? t('results') : t('result') })}
          </p>
        </>
      )}

      {/* 商品网格 */}
      <div className={`product-grid  ${getGridClass()}`}>
        {listDataWithAdv.map((product, index) => {
          if (product.data_type === 'adv') {
            return (
              <AdvCard
                key={product.adv_id}
                product={product}
                sdProductListPageADClick={sdProductListPageADClick}
                sdProductListPageADExp={sdProductListPageADExp}
                column={column}
                isWeb={isWeb}
                index={index}
                isCrowdRecommendation={isCrowdRecommendation}
              />
            );
          }
          return (
            <ProductCard
              key={product.shop_sku}
              product={product as any}
              index={index}
              isAcc={isAcc}
              imageRule={imageRule}
              imageHoverRule={imageHoverRule}
              showTryOn
              showReviewStar={!isAcc}
              showMember
              handelClick={handleProductClick}
              onInView={handleProductInView}
              onValidExposure={handleProductValidInView}
              collectScene="坑位收藏"
              showWishNum={false}
              loginSituation="商品列表页"
              showCorrelation={!isAcc}
              showCorrelationCount={isWeb || column === 1 ? 6 : 3}
              imageClassName={imageClassName}
              SDTryonClick={SDProductListPageTryon_Click}
              frameWidth={getPicWidth()}
            />
          );
        })}
      </div>
      {/* 移动端无限滚动分页 */}
      {!isWeb && (
        <InfiniteScroll
          onReachBottom={handleReachBottom}
          loading={loading}
          page={infiniteScrollPage}
          total={allTotal}
          limit={limit}
          showNoMore={showNoMore}
        />
      )}
      {isWeb && allTotal > limit && (
        <Pagination
          total={allTotal}
          current={page}
          pageSize={limit}
          onChange={(_, _page) => handlePageChange(_page)}
          className="mt-[77px]"
        />
      )}
    </>
  );
}
